import { supabase } from './supabase'

/**
 * Global search.
 *
 * spec.md §122: "Rare, so it may be slower — but it must actually work,
 * because the day you need it you *really* need it." Everything here is one
 * database call; there is no client-side filtering to drift out of step with
 * what the server thinks matches.
 *
 * Row-level security applies normally — `search_everything` runs as the caller
 * — so a member searching finds only what a member may read.
 */

export type HitKind = 'payment' | 'letter' | 'document'

export interface Hit {
  kind: HitKind
  id: string
  spaceId: string
  title: string
  snippet: string | null
  occurredOn: string | null
  amountCents: number | null
  rank: number
}

export async function search(query: string, limit = 40): Promise<Hit[]> {
  const trimmed = query.trim()
  if (trimmed.length < 2) return []

  const { data, error } = await supabase.rpc('search_everything', {
    p_query: trimmed,
    p_limit: limit,
  })
  if (error) throw error

  return (data ?? []).map((row: Record<string, unknown>) => ({
    kind: row.kind as HitKind,
    id: row.id as string,
    spaceId: row.space_id as string,
    title: (row.title as string) ?? '',
    snippet: (row.snippet as string) ?? null,
    occurredOn: (row.occurred_on as string) ?? null,
    amountCents: row.amount_cents === null ? null : Number(row.amount_cents),
    rank: Number(row.rank ?? 0),
  }))
}

export interface Segment {
  text: string
  match: boolean
}

/**
 * Turn Postgres's `<b>` markers into something safe to render.
 *
 * `ts_headline` returns HTML, and putting OCR text through
 * dangerouslySetInnerHTML would mean a supplier could put script tags on an
 * invoice and have them run in the app. Splitting into segments and letting
 * React escape each one costs nothing and removes the question entirely.
 */
export function highlight(snippet: string | null): Segment[] {
  if (!snippet) return []
  return snippet
    .split(/(<b>.*?<\/b>)/g)
    .filter(Boolean)
    .map((part) =>
      part.startsWith('<b>')
        ? { text: part.slice(3, -4), match: true }
        : { text: part, match: false },
    )
}

export function groupHits(hits: Hit[]): { kind: HitKind; label: string; hits: Hit[] }[] {
  const labels: Record<HitKind, string> = {
    payment: 'Betalingen',
    letter: 'Brieven',
    document: 'Documenten',
  }
  return (['payment', 'letter', 'document'] as HitKind[])
    .map((kind) => ({ kind, label: labels[kind], hits: hits.filter((h) => h.kind === kind) }))
    .filter((group) => group.hits.length > 0)
}
